import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { z } from 'zod';
import { Mag } from '../components/mag.js';
import { magToast } from '../components/toaster.js';
import {
  finalizeMetaConnect,
  type MetaConnectHandoffPage,
  readMetaConnectHandoff,
} from '../lib/api.js';
import { env } from '../lib/env.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { brandsQuery } from '../lib/queries.js';

// /meta-connect/picker — landing page after Facebook redirects the
// founder back from OAuth (s191). The handoff query param is a
// short-lived token the api stashed at callback time; we read it,
// render the user's Facebook pages, and finalize on click.
//
// Notes:
//   - The handoff is single-shot: once finalize succeeds it's
//     consumed. If the founder refreshes the page after finalize
//     they'll see "handoff expired" — that's correct, they should
//     navigate to settings to see the connected state.
//   - We have to look up the brand slug to navigate back to settings,
//     but the handoff only carries brand id. Fetch brands to map.

const searchSchema = z.object({
  handoff: z.string().min(1).max(200),
});

export const Route = createFileRoute('/brand/$slug/meta-connect/picker')({
  validateSearch: (s) => searchSchema.parse(s),
  component: MetaConnectPickerPage,
});

function MetaConnectPickerPage() {
  const { t } = useLingui();

  const navigate = useNavigate();
  const { handoff } = Route.useSearch();
  // s191.3 — brand-aware URL. The slug is in the path so back +
  // retry links work even after the handoff expires.
  const { slug } = Route.useParams();
  const [finalizingPageId, setFinalizingPageId] = useState<string | null>(null);

  const handoffQuery = useQuery({
    queryKey: ['meta-connect-handoff', handoff],
    queryFn: () => readMetaConnectHandoff(handoff),
    retry: false,
  });
  // Resolve brand by slug so we can look up its id when finalizing.
  // The handoff also carries the brandId (server-validated against
  // our state token); we cross-check it matches the slug-resolved
  // id before finalize to guard against URL tampering.
  const brandsQ = useQuery(brandsQuery());
  const brandFromSlug = brandsQ.data?.find((b) => b.slug === slug) ?? null;

  // Where to send the founder to retry the OAuth dance. Brand-id
  // resolved from URL slug → works even after the handoff is gone.
  const retryUrl = brandFromSlug
    ? `${env.API_URL}/api/brands/${brandFromSlug.id}/meta/connect/start`
    : null;

  const onPick = async (page: MetaConnectHandoffPage) => {
    if (!handoffQuery.data) return;
    if (!page.instagramBusinessId) {
      magToast.error(
        i18n._(msg`no instagram on this page.`),
        i18n._(msg`link an instagram business account in facebook business settings first.`),
      );
      return;
    }
    setFinalizingPageId(page.id);
    try {
      await finalizeMetaConnect(handoffQuery.data.brandId, handoff, page.id);
      magToast.success(i18n._(msg`connected.`), i18n._(msg`mag can publish to this account now.`));
      const returnTo = handoffQuery.data.returnTo;
      // The channels-page "connect meta ads" row sets returnTo to the
      // bare channels index. But finalizing the meta connection always
      // (re)activates the instagram organic channel (server upserts
      // brand_channels.status='active'), so dropping the founder on the
      // index — where instagram now reads ACTIVE but they're a click
      // away from it — is the wrong home. Redirect that one case to the
      // instagram channel detail (?intake=open mounts the onboarding
      // chat, matching the other organic connect entry points). All
      // other returnTos (/channels/instagram from the detail/feed
      // connect, /campaigns/import from the paid-ads flow) are honored
      // verbatim, and an absent returnTo keeps the settings fallback.
      const channelsIndex = `/brand/${slug}/channels`;
      if (returnTo === channelsIndex) {
        window.location.assign(`${channelsIndex}/instagram?intake=open`);
        return;
      }
      if (returnTo) {
        window.location.assign(returnTo);
        return;
      }
      await navigate({
        to: '/brand/$slug/settings/integrations',
        params: { slug },
      });
    } catch (err) {
      magToast.error("couldn't finalize.", founderErrorCopy(err));
      setFinalizingPageId(null);
    }
  };

  if (handoffQuery.isLoading) {
    return (
      <PickerShell backToBrandSlug={slug} step="reading">
        <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--ink)]/55">
          {'↘'} <Trans>reading meta's reply…</Trans>
        </p>
      </PickerShell>
    );
  }
  if (handoffQuery.isError || !handoffQuery.data) {
    return (
      <PickerShell backToBrandSlug={slug} step="pick page">
        <article className="rounded-2xl border-[1.5px] border-[var(--plum)]/40 bg-[var(--spell-soft)]/40 p-5">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>this link has expired</Trans>
          </p>
          <p className="mt-2 font-serif text-[16px] leading-[1.5] text-[var(--ink)]/85">
            <Trans>
              “the connect handoff isn't valid anymore — either it expired (5 min cap) or someone
              already used it. no harm done, just start over.”
            </Trans>
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            <Link to="/brand/$slug/channels" params={{ slug }} className="link-quiet text-[12px]">
              {'↘'} <Trans>open channels</Trans>
            </Link>
            {retryUrl ? (
              <a href={retryUrl} className="btn">
                <Trans>try connecting again</Trans>
                <span className="btn-arrow">→</span>
              </a>
            ) : null}
          </div>
        </article>
      </PickerShell>
    );
  }

  const pages = handoffQuery.data.pages;
  const publishable = pages.filter((p) => p.instagramBusinessId);
  const noIg = pages.filter((p) => !p.instagramBusinessId);

  // No pages at all → the founder needs to create one in Facebook
  // first. We can't do this on their behalf — Meta doesn't expose a
  // page-create API to apps.
  if (pages.length === 0) {
    return (
      <PickerShell backToBrandSlug={slug} step="pick page">
        <p className="font-serif text-[16px] leading-[1.5] text-[var(--ink)]/85">
          <Trans>
            “i need a facebook page to publish to instagram — that's how meta's api works. create a
            page, link your instagram business account to it, then come back. takes ~2 minutes.”
          </Trans>
        </p>
        <ol className="mt-6 flex flex-col gap-4">
          <PickerStep
            n={1}
            label={t`create a facebook page`}
            note={t`i'll wait. name it whatever your brand is.`}
            actionLabel={t`open facebook · create page`}
            actionHref="https://www.facebook.com/pages/create"
          />
          <PickerStep
            n={2}
            label={t`link your instagram to that page`}
            note={t`on instagram app: settings → account type and tools → switch to professional account → business → settings → linked accounts → facebook → pick the page you just created.`}
            actionLabel={t`meta business help`}
            actionHref="https://www.facebook.com/business/help/connect-instagram-to-page"
          />
          {retryUrl ? (
            <PickerStep
              n={3}
              label={t`retry the connect`}
              note={t`once the page exists + your ig is linked, come back here.`}
              actionLabel={t`try connecting again`}
              actionHref={retryUrl}
              primary
            />
          ) : null}
        </ol>
      </PickerShell>
    );
  }

  return (
    <PickerShell backToBrandSlug={slug} step="pick page">
      <p className="font-serif text-[17px] leading-[1.45] text-[var(--ink)]/85">
        <Trans>
          “pick the facebook page mag should post on behalf of. i'll use the instagram business
          account attached to it.”
        </Trans>
      </p>

      {publishable.length > 0 ? (
        <ul className="mt-7 flex flex-col gap-3">
          {publishable.map((page) => (
            <PageRow
              key={page.id}
              name={page.name}
              instagramBusinessId={page.instagramBusinessId ?? ''}
              onPick={() => void onPick(page)}
              finalizing={finalizingPageId === page.id}
              anyFinalizing={finalizingPageId !== null}
            />
          ))}
        </ul>
      ) : null}

      {/* Pages without an IG attached. When there are NONE publishable
          (the founder has pages but none ready), promote this to an
          action block with a retry button — they need to link IG to
          one of their pages, then come back. When there ARE
          publishable pages alongside, keep the dimmer informational
          treatment. */}
      {noIg.length > 0 && publishable.length === 0 ? (
        <div className="mt-6 rounded-xl border-[1.5px] border-[var(--plum)]/40 bg-[var(--spell-soft)]/40 p-5">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>no page has instagram linked yet</Trans>
          </p>
          <p className="mt-2 text-[13.5px] leading-[1.5] text-[var(--ink)]/80">
            <Trans>
              i can see your pages — but none have an instagram business account attached. pick one
              below + link your instagram to it, then come back and retry.
            </Trans>
          </p>
          <ul className="mt-3 flex flex-col gap-1">
            {noIg.map((page) => (
              <li
                key={page.id}
                className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/65"
              >
                ↘ {page.name}
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            <a
              href="https://www.facebook.com/business/help/connect-instagram-to-page"
              target="_blank"
              rel="noreferrer"
              className="link-quiet text-[11px]"
            >
              <Trans>meta business help</Trans>
            </a>
            {retryUrl ? (
              <a href={retryUrl} className="btn">
                <Trans>try connecting again</Trans>
                <span className="btn-arrow">→</span>
              </a>
            ) : null}
          </div>
        </div>
      ) : noIg.length > 0 ? (
        <div className="mt-6 rounded-xl border border-dashed border-[var(--ink)]/15 bg-[var(--paper)] p-4">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/55">
            {'↘'} <Trans>pages without an instagram business account</Trans>
          </p>
          <p className="mt-2 text-[13px] leading-[1.5] text-[var(--ink)]/65">
            <Trans>
              these aren't connectable until you link an instagram business account to them:
            </Trans>
          </p>
          <ul className="mt-2 flex flex-col gap-1">
            {noIg.map((page) => (
              <li
                key={page.id}
                className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55"
              >
                ↘ {page.name}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </PickerShell>
  );
}

/** One numbered step in the "no pages" empty state. Renders a label,
 *  a sub-note explaining what to do, and either an external link
 *  (open in new tab) or the primary retry action that takes the
 *  founder back through the OAuth dance. */
function PickerStep({
  n,
  label,
  note,
  actionLabel,
  actionHref,
  primary,
}: {
  n: number;
  label: string;
  note: string;
  actionLabel: string;
  actionHref: string;
  primary?: boolean;
}) {
  const external = actionHref.startsWith('http');
  return (
    <li className="flex items-start gap-4 rounded-xl border-[1.5px] border-[var(--line)] bg-white p-5">
      <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-[1.5px] border-[var(--ink)] bg-[var(--spell)] font-mono text-[12px] font-bold text-[var(--ink)]">
        {n}
      </span>
      <div className="flex-1">
        <p className="font-display text-[18px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
          {label}
        </p>
        <p className="mt-1.5 text-[13px] leading-[1.5] text-[var(--ink)]/65">{note}</p>
      </div>
      <a
        href={actionHref}
        {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
        className={primary ? 'btn' : 'btn btn-ghost text-[11px]'}
      >
        {actionLabel}
        <span className="btn-arrow">→</span>
      </a>
    </li>
  );
}

/** A single connectable Facebook page. Brand-aligned card: ink
 *  border, hand-edge plum shadow, hover-lift. Group hook on the
 *  card means the inner button's arrow animates on whole-card hover
 *  — the entire row is the affordance, not just the button. */
function PageRow({
  name,
  instagramBusinessId,
  onPick,
  finalizing,
  anyFinalizing,
}: {
  name: string;
  instagramBusinessId: string;
  onPick: () => void;
  finalizing: boolean;
  anyFinalizing: boolean;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        disabled={anyFinalizing}
        className="group flex w-full items-center justify-between gap-4 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-4 text-left shadow-[3px_3px_0_var(--plum)] transition-all duration-150 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[5px_5px_0_var(--plum)] disabled:cursor-not-allowed disabled:opacity-60 sm:p-5"
      >
        <div className="min-w-0">
          <p className="truncate font-display text-[17px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]">
            {name}
          </p>
          <p className="mt-1 truncate font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            <Trans>↘ ig business id · {instagramBusinessId}</Trans>
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2 font-display text-[13px] font-bold text-[var(--spell)]">
          {finalizing ? <Trans>connecting…</Trans> : <Trans>pick this</Trans>}
          <span aria-hidden className="mag-arrow mag-arrow-e">
            →
          </span>
        </span>
      </button>
    </li>
  );
}

function PickerShell({
  children,
  backToBrandSlug,
  step,
}: {
  children: ReactNode;
  /** When known (handoff carried a brand we can resolve), link
   *  back to that brand's integrations settings. Otherwise back
   *  link points at the brands list. */
  backToBrandSlug?: string | undefined;
  /** Right-hand pill in the header strip. Reads as a tiny step
   *  indicator ("pick page", "reading", "expired"). Keeps the
   *  wizard feel even though the flow is just one screen. */
  step?: string;
}) {
  return (
    <main className="min-h-screen bg-[var(--cream)]">
      {/* Sticky header strip matches the brief/onboarding wizards:
          magig wordmark on the left, step indicator + exit on the
          right. Founders land here mid-OAuth from Facebook — the
          wordmark grounds them back inside the magig app. */}
      <header className="sticky top-0 z-10 flex h-14 items-center justify-between border-b border-[var(--line)] bg-[var(--paper)] px-4 sm:px-7">
        <Link to="/home" className="flex items-center gap-2 no-underline" aria-label="magig">
          <span className="relative font-display text-[18px] font-black tracking-[-0.04em] text-[var(--ink)]">
            magig
            <span className="absolute -top-0.5 left-[52%] text-[6px] leading-none text-[var(--spell)]">
              ✦
            </span>
          </span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          {step ? (
            <span className="inline-flex items-center rounded-full bg-[var(--spell)] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink)]">
              <Trans>step 2 · {step}</Trans>
            </span>
          ) : null}
          {backToBrandSlug ? (
            <Link
              to="/brand/$slug/settings/integrations"
              params={{ slug: backToBrandSlug }}
              className="btn btn-ghost btn-sm"
            >
              <Trans>exit</Trans>
            </Link>
          ) : null}
        </div>
      </header>

      <section className="mx-auto max-w-[760px] px-4 pb-20 pt-12 sm:px-7">
        <div className="flex items-start gap-4">
          <Mag size={64} mood="smug" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>connect meta</Trans>
            </p>
            <h1 className="mt-2 font-display text-[30px] font-black leading-[1.05] tracking-[-0.04em] sm:text-[40px]">
              <Trans>pick the right page.</Trans>
            </h1>
            <div className="mt-5">{children}</div>
          </div>
        </div>
      </section>
    </main>
  );
}
